/* eslint-disable max-len */
import React, {
  FC, memo, useState, useEffect, useRef, useCallback,
} from 'react';
import './Recognize.less';
import './slice/slice';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button } from 'antd';
import ReactCardFlip from 'react-card-flip';
import { shuffle } from 'lodash';
import FixWidthContent from '../../components/FixWidthContent';
import withTagManager from '../../hooks/withTagManager';
import { WordFromList } from '../../components/Recognize/WordFromList';
import { ReactComponent as Repeat } from '../../assets/icons/games/recognize/refresh-ccw-rec.svg';
import { ReactComponent as Correct } from '../../assets/icons/games/recognize/check-circle-2.svg';
import InfoButton from '../../components/InfoButton/InfoButton';
import RulesOfTheGame from './RulesOfTheGame';
import { getActiveList, getUserData } from '../auth/slice/selectors';
import { BASE_API_URL } from '../../services/ApiService';
import EndGameInfo from '../../components/EndGameInfo/EndgameInfo';
import useGoToRoute from '../../hooks/useGoToRoute';
import { Path } from '../../constants/Path';
import useAudioPlayer from '../../hooks/useAudioPlayer';
import { RightSideButtons } from '../../components/RightSideButtons/RightSideButtons';
import { EditTermButton } from '../../components/EditTermModal/EditTermButton';
import { usePolishAudio } from '../../hooks/usePolishAudio';
import { PolishAudioButton } from '../../components/PolishAudioButton/PolishAudioButton';
import { Roles } from '../../constants/Roles';
import { cleanTextForTTS } from '../../utils/textBracketSanitize';
import { getListComposition, getListWordId, withListTexts } from '../../utils/gameWords';
import WordCounter from '../../components/WordCounter';
import { EditListButton } from '../../components/EditListButton/EditListButton';
import { FitText } from '../../components/FitText/FitText';
import useActivityTracking from '../../hooks/useActivityTracking';
import { ActivityGame, recordAnswer } from '../../services/activity';

export const RecognizeView: FC = memo(() => {
  const { t } = useTranslation();
  const activeList = useSelector(getActiveList);
  const { words } = activeList || { words: [] };

  const [currentWords, setCurrentWords] = useState(() => shuffle(words));
  const [wordOnScreen, setWordOnScreen] = useState(0);
  const [listEndInfo, setListEndInfo] = useState(false);
  const [isFlipped, setIsFlipped] = useState(false);

  const user = useSelector(getUserData);
  const canEditTerms = user?.role === Roles.TEACHER;

  useActivityTracking(ActivityGame.RECOGNIZE);

  const { play: playAudio, stop: stopAudio } = useAudioPlayer();

  const {
    playPolish,
    togglePlayPolish,
    playPolishAudio,
  } = usePolishAudio({ storageKey: 'recognize_playPolish' });

  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const goToNext = useGoToRoute(Path.RECOGNIZE2);

  const repeatGame = useCallback(() => {
    stopAudio();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setCurrentWords(shuffle(words || []));
    setListEndInfo(false);
    setWordOnScreen(0);
    setIsFlipped(false);
    setIsTransitioning(false);
  }, [stopAudio, words]);

  // Gra zaczyna od nowa przy innym składzie listy (inna lista, dodane albo usunięte słowa). Poprawka tekstu,
  // np. z „Edytuj zwrot”, gry nie przerywa: słowa w grze dostają nowy tekst, a gra zostaje na tym samym słowie
  const listCompositionRef = useRef('');

  useEffect(() => {
    const listComposition = getListComposition(words);

    if (listComposition === listCompositionRef.current) {
      setCurrentWords((prev) => withListTexts(prev, words));

      return;
    }

    listCompositionRef.current = listComposition;
    setCurrentWords(shuffle(words));
    setWordOnScreen(0);
    setIsFlipped(false);
    setListEndInfo(false);
  }, [words]);

  const handleFlip = useCallback((withSound: boolean = false) => {
    if (isTransitioning) return;

    // 1. ZAWSZE i NATYCHMIAST przełączamy stan wizualny, jeśli nie ma dźwięku
    if (!withSound) {
      setIsFlipped((current) => !current);

      return;
    }
    setIsFlipped((current) => !current);

    const current = currentWords[wordOnScreen];
    if (!current) {
      return;
    }

    // Pozostała logika audio
    const { term, definition } = current;
    const cleanTerm = cleanTextForTTS(term);
    if (!cleanTerm) {
      return;
    }

    const englishUrl = `${BASE_API_URL}/v1/tts?q=${encodeURIComponent(cleanTerm)}`;

    playAudio(englishUrl, () => {
      setTimeout(() => {
        playPolishAudio(definition);
      }, 200);
    }).catch(() => {});

    // ---------------------------------------------

    // USUWAMY TĄ LINIĘ: setIsFlipped((current) => !current);
    // Bo została przeniesiona na początek funkcji, aby działała natychmiast!
  }, [currentWords, wordOnScreen, playAudio, isTransitioning, playPolishAudio]);

  const handleNextClick = useCallback((): void => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    // CORRECT to samoocena ucznia — gra nie zna wyniku. Tak samo REPEAT niżej; „?” (podgląd tłumaczenia) to nie próba
    recordAnswer(null, currentWords[wordOnScreen]?.term);

    stopAudio();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (wordOnScreen < currentWords.length - 1) {
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
        setIsTransitioning(false);
      }, 100);
    } else {
      setListEndInfo(true);
      setIsTransitioning(false);
    }
  }, [isTransitioning, stopAudio, wordOnScreen, currentWords]);

  const handleRepeat = useCallback((): void => {
    if (isTransitioning) return;
    setIsTransitioning(true);

    const word = currentWords[wordOnScreen];
    if (!word) {
      setIsTransitioning(false);

      return;
    }

    recordAnswer(null, word.term);

    const wordToRepeat = {
      ...word,
      id: `${word.id}_repeat_${Date.now()}`,
      listWordId: getListWordId(word),
    };

    stopAudio();

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (wordOnScreen === currentWords.length - 1) {
      // Przed powtórką wchodzi inne słowo — pierwsze z listy, ale nie to z błędem (mogło stać na początku
      // i wrócić na koniec z wcześniejszego REPEAT). Gdy innego nie ma (wszystkie słowa zapisane tak samo), słowo wraca od razu
      const filler = currentWords.find(({ term }) => term !== wordToRepeat.term);
      const updatedCurrentWords = filler ? [...currentWords, filler, wordToRepeat] : [...currentWords, wordToRepeat];
      setCurrentWords(updatedCurrentWords);
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
        setIsTransitioning(false);
      }, 100);
    } else {
      setCurrentWords((prev) => [...prev, wordToRepeat]);
      setIsFlipped(false);
      timeoutRef.current = setTimeout(() => {
        setWordOnScreen((prev) => prev + 1);
        timeoutRef.current = null;
        setIsTransitioning(false);
      }, 100);
    }
  }, [isTransitioning, currentWords, wordOnScreen, stopAudio]);

  useEffect(() => () => {
    stopAudio();
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [stopAudio]);

  if (!currentWords || currentWords.length === 0) {
    return (
      <FixWidthContent>
        <div>{t('games:states.noWords')}</div>
      </FixWidthContent>
    );
  }

  const current = currentWords[wordOnScreen];

  return (
    <>
      <FixWidthContent className="recognize-main">
        {!listEndInfo && (
        <WordCounter
          current={wordOnScreen + 1}
          total={currentWords.length}
        />
        )}
        {/* Na telefonie trzymanym poziomo karta ze słowem i karta „?” stoją obok siebie (Recognize.less) */}
        {!listEndInfo && current && (
          <div className="recognize-stage">
            <div>
              <WordFromList
                id={current.id}
                content={current.term}
              />
            </div>

            <div className="flipper">
              <ReactCardFlip
                isFlipped={isFlipped}
                flipDirection="vertical"
                flipSpeedFrontToBack={0.15}
                flipSpeedBackToFront={0.15}
              >
                <div
                  className="front"
                  onClick={() => handleFlip(true)}
                  aria-hidden="true"
                >
                  ?
                </div>
                {/* Długi zwrot dostaje mniejszą czcionkę, żeby zmieścił się w karcie (na telefonie) */}
                <FitText
                  className="back"
                  onClick={() => handleFlip(false)}
                  aria-hidden="true"
                >
                  {current.definition}
                </FitText>
              </ReactCardFlip>
            </div>

            <div className="options">
              <Button
                onClick={handleRepeat}
                className={wordOnScreen === currentWords.length - 1 ? 'optionButton lastWord' : 'optionButton'}
                disabled={isTransitioning}
                aria-label={t('games:labels.repeatWord')}
              >
                <Repeat className="btnLabel" />
                <br />
                {t('games:buttons.repeat')}
              </Button>

              <Button
                onClick={handleNextClick}
                className="optionButton"
                disabled={isTransitioning}
                aria-label={t('games:labels.nextWord')}
              >
                <Correct className="btnLabel" />
                <br />
                {t('games:buttons.correct')}
              </Button>
            </div>
          </div>
        )}

        {listEndInfo && (
          <EndGameInfo repeatGameTrigger={repeatGame} nextGame={goToNext} />
        )}

        <RightSideButtons>
          <InfoButton content={<RulesOfTheGame />} />

          {canEditTerms && (
            <EditTermButton
              wordId={getListWordId(current)}
              currentTerm={current.term}
              currentDefinition={current.definition}
              listId={activeList?.id || ''}
              onTermUpdated={(newTerm, newDefinition) => {
                const listWordId = getListWordId(current);

                setCurrentWords((prev) => prev.map((word) => (getListWordId(word) === listWordId
                  ? { ...word, term: newTerm, definition: newDefinition }
                  : word)));
              }}
            />
          )}

          <PolishAudioButton
            playPolish={playPolish}
            onToggle={togglePlayPolish}
          />

          {canEditTerms && activeList?.id && (
            <EditListButton listId={activeList.id} />
          )}
        </RightSideButtons>
      </FixWidthContent>
    </>
  );
});

export default withTagManager(RecognizeView);
